import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus, ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { useOptimizedTasks } from "@/hooks/useOptimizedTasks";
import { useFamily } from "@/hooks/useFamily";
import { useTaskLists } from "@/hooks/useTaskLists";
import { SimpleTaskItem } from "@/components/tasks/SimpleTaskItem";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useCurrentSection } from "@/hooks/useCurrentSection";
import { useAuth } from "@/hooks/useAuth";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';

export function TaskListDetailView() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { family, familyMembers } = useFamily();
  const { taskLists } = useTaskLists(family?.id);
  const { tasks, loading, completeTask, updateTask, deleteTask, refreshTasks, updateTasksOrder } = useOptimizedTasks(family?.id);
  const [showCompleted, setShowCompleted] = useState(false);
  const { primaryVariant, secondaryVariant } = useCurrentSection();
  const { user } = useAuth();
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = pendingTasks.findIndex(task => task.id === active.id);
      const newIndex = pendingTasks.findIndex(task => task.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedTasks = arrayMove(pendingTasks, oldIndex, newIndex);
        const orderedIds = reorderedTasks.map(task => task.id);
        updateTasksOrder(orderedIds);
      }
    }
  };
  
  // Get current user's profile id from the family
  const currentUserProfile = familyMembers?.find(fm => 
    fm.profile_id && fm.profiles && 
    (fm.profiles as any).user_id === user?.id
  );
  const currentUserProfileId = currentUserProfile?.profile_id;

  const taskList = taskLists.find(list => list.id === listId);
  const listTasks = tasks.filter(task => task.list_id === listId);
  const pendingTasks = listTasks.filter(task => task.status === 'pending');
  const completedTasks = listTasks.filter(task => task.status === 'completed');


  if (!taskList) {
    return (
      <div className="min-h-screen bg-page-gradient flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Liste introuvable</h2>
          <Button onClick={() => navigate("/tasks")}>
            Retour aux listes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page-gradient">
      {/* Header */}
      <div className="flex items-center px-4 pt-2 pb-4 bg-background">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/tasks")}
          className="hover:bg-transparent border-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        
        <div className="flex items-center space-x-3 ml-3 flex-1">
          <span className="text-xl">{taskList.icon}</span>
          <h1 className="text-lg font-semibold">{taskList.name}</h1>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          aria-label="Éditer la liste"
          onClick={() => navigate(`/task-list-settings/${listId}`)}
          className="hover:bg-transparent border-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <Pencil className="h-4 w-4 text-foreground" />
        </Button>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-8">
        {/* Active Tasks Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Tâches actives ({pendingTasks.length})</h2>
          </div>
          
          {loading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-14 bg-muted/50 animate-pulse rounded-md" />
              ))}
            </div>
          ) : pendingTasks.length === 0 ? (
            <div className="text-center py-8 space-y-4">
              <div className="text-4xl">🎯</div>
              <div className="space-y-1">
                <p className="text-lg font-medium text-muted-foreground">Aucune tâche active</p>
                <p className="text-sm text-muted-foreground">Ajoutez votre première tâche pour commencer !</p>
              </div>
            </div>
          ) : (
            <DndContext 
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
              <SortableContext 
                items={pendingTasks.map(task => task.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {pendingTasks.map((task) => (
                    <SimpleTaskItem
                      key={task.id}
                      task={task}
                      onCompleteTask={completeTask}
                      currentUserProfileId={currentUserProfileId}
                      onTaskCompleted={() => setShowCompleted(true)}
                      familyId={family?.id}
                      isDraggable={true}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
          
          {/* Add Task Button */}
          <Button
            variant="ghost"
            onClick={() => navigate(`/tasks/create?listId=${listId}`)}
            className="w-full border-2 border-dashed border-muted-foreground/30 rounded-lg p-4 bg-background hover:bg-muted/20 transition-colors group min-h-[2.5rem]"
          >
            <div className="flex items-center justify-center gap-2 text-muted-foreground group-hover:text-foreground">
              <Plus className="h-4 w-4" />
              <span className="text-sm font-medium">Ajouter une tâche</span>
            </div>
          </Button>
        </div>

        {/* Completed Tasks Section */}
        {completedTasks.length > 0 && (
          <Collapsible open={showCompleted} onOpenChange={setShowCompleted}>
            <div className="space-y-4">
              <CollapsibleTrigger asChild>
                <button className="w-full flex items-center justify-between py-3 px-4 hover:bg-muted/30 transition-colors rounded-lg group">
                  <h2 className="text-xl font-semibold text-left">Tâches terminées ({completedTasks.length})</h2>
                  <div className="p-1 rounded-md group-hover:bg-muted/50 transition-colors">
                    {showCompleted ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-3">
                  {completedTasks.map((task) => (
                    <SimpleTaskItem
                      key={task.id}
                      task={task}
                      onCompleteTask={completeTask}
                      currentUserProfileId={currentUserProfileId}
                      familyId={family?.id}
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        )}
      </div>
    </div>
  );
}